// On-spot registrations at an offline-capable desk. Every submission carries an ID made on the device, so a
// registration saved while the network is down can print its badge straight away and be uploaded later, and a
// retry after a lost response returns the first registration instead of creating a second one.
import { enqueuePendingRegistration, putRegistration, __internal } from './offlineDb';
import { generateQrBase64 } from './qrCode';

// Same alphabet as the server check (no I/O/0/1); the "-O" marker never appears in server-made IDs.
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const OFFLINE_ID = /^[A-Z0-9]{1,3}-O[A-HJ-NP-Z2-9]{6}$/;
export const QUEUE_EVENT = 'offline:queue';

const randomChars = (length) => {
  const bytes = new Uint8Array(length);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join('');
};

/** DEL-O7KQ2MZ: category prefix like server IDs, then the offline marker. */
export const newOfflineId = (formData = {}) => {
  const category = Object.keys(formData).find((key) => key.toLowerCase() === 'category');
  const prefix = String((category && formData[category]) || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3) || 'OS';
  return `${prefix}-O${randomChars(6)}`;
};

export const notifyQueueChange = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(QUEUE_EVENT));
};

/** Saves the registration on this device and queues it for upload. Returns the same shape as the server. */
export async function registerOffline(eventId, payload) {
  const userId = payload.offlineId;
  const free = payload.paymentStatus !== 'pending';
  const status = free ? 'REGISTERED' : 'PENDING_FOR_APPROVAL';
  const now = new Date().toISOString();
  await putRegistration(__internal.toStoredRegistration(eventId, {
    userId,
    formData: payload.formData || {},
    source: payload.source,
    status,
    registeredAt: now,
    createdAt: now,
    offlinePending: true,
  }));
  await enqueuePendingRegistration({
    eventId,
    userId,
    name: displayName(payload.formData),
    payload: { ...payload, offlineQueued: true },
  });
  notifyQueueChange();
  return { userId, status, qrCodeUrl: await generateQrBase64(userId), offline: true };
}

export const displayName = (formData = {}) => {
  const key = Object.keys(formData).find((k) => k.toLowerCase().includes('name'));
  return key ? String(formData[key] || '') : '';
};
